import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useQuizSession } from '../hooks/useQuizSession'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import ConfirmDialog from '../components/ConfirmDialog'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import SendIcon from '@mui/icons-material/Send'
import { UpgradeCard } from '../components/Premium'
import { AiMessage, hasTutorContent, ConfidenceSection, StreamingSegmentBlock, TypingDots, UserBubble } from '../components/quiz/Chat'
import { SessionCompleteContent } from '../components/quiz/SessionComplete'

const RETRY_MESSAGES = [
  'This is taking a bit longer...',
  'Groq is busy, hang tight...',
  'Almost there, just a moment...',
]

const FIRST_QUESTION_TASK = 'Answer from memory. A tutor helps you along.'


export default function Quiz() {
  const navigate = useNavigate()
  const { session } = useAuth()
  const token = session?.access_token ?? null
  const { state, startSession, setDraftAnswer, submitAnswer, skipQuestion, nextQuestion, refresh, clearConversationError } = useQuizSession(token)

  // Set when user clicks Send; cleared when ANSWER_COMPLETE lands in history
  const [pendingAnswer, setPendingAnswer] = useState<string | null>(null)
  // Set when user selects confidence; cleared with pendingAnswer
  const [pendingConfidenceLabel, setPendingConfidenceLabel] = useState<string | null>(null)
  const [showSkipDialog, setShowSkipDialog] = useState(false)
  const [retryMsgIdx, setRetryMsgIdx] = useState(0)

  const messageEndRef = useRef<HTMLDivElement>(null)
  const historyLength = state.phase === 'conversation' ? state.conversationHistory.length : 0
  const currentStreamingText = state.phase === 'conversation' ? state.streamingText : null
  const isRetrying = state.phase === 'conversation' ? state.isRetrying : false

  // Cleared during render, not in an effect (react-hooks/set-state-in-effect).
  const [trackedHistoryLength, setTrackedHistoryLength] = useState(historyLength)
  if (historyLength !== trackedHistoryLength) {
    setTrackedHistoryLength(historyLength)
    setPendingAnswer(null)
    setPendingConfidenceLabel(null)
  }

  // Reset during render, not in the interval effect (react-hooks/set-state-in-effect).
  const [trackedRetrying, setTrackedRetrying] = useState(isRetrying)
  if (isRetrying !== trackedRetrying) {
    setTrackedRetrying(isRetrying)
    if (!isRetrying) setRetryMsgIdx(0)
  }

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [historyLength, currentStreamingText, pendingAnswer, pendingConfidenceLabel])

  useEffect(() => {
    if (!isRetrying) return
    const id = setInterval(() => setRetryMsgIdx(i => (i + 1) % RETRY_MESSAGES.length), 15_000)
    return () => clearInterval(id)
  }, [isRetrying])

  if (state.phase === 'loading') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 8 }}>
        <LoadingSpinner />
        <Typography color="text.secondary">Loading your session...</Typography>
      </Box>
    )
  }

  if (state.phase === 'error') {
    return (
      <Box sx={{ maxWidth: 480 }}>
        <ErrorMessage message={state.message} />
        <Button variant="contained" onClick={refresh} sx={{ mt: 2 }}>Try again</Button>
      </Box>
    )
  }

  if (state.phase === 'limit') {
    // Premium upsell, not "Try again" (which would re-hit the cap). Premium is bought in the app, not on web.
    return (
      <Box sx={{ py: 4 }}>
        <UpgradeCard title="One review session a day" feature="unlimited daily reviews" />
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button variant="text" onClick={() => navigate('/today')}>Back to Today</Button>
        </Box>
      </Box>
    )
  }

  if (state.phase === 'empty') {
    return (
      <Box sx={{ maxWidth: 480 }}>
        <Card elevation={1}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 30, letterSpacing: '-0.5px', mb: 1 }}>All caught up</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {state.nextReviewInHours > 0
                ? `Next review in ${state.nextReviewInHours} hour${state.nextReviewInHours === 1 ? '' : 's'}.`
                : 'No reviews due. Keep adding books to grow your library.'}
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/today')}>Back to Today</Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (state.phase === 'summary') {
    const concepts = state.session.concepts
    const bookCounts = concepts.reduce<Record<string, number>>((acc, q) => {
      acc[q.bookTitle] = (acc[q.bookTitle] ?? 0) + 1
      return acc
    }, {})
    const topics = [...new Set(concepts.map(q => q.question.specificTopic).filter(Boolean))]
    const estimatedMinutes = Math.ceil(concepts.length * 1.5)

    return (
      <Box sx={{ maxWidth: 480 }}>
        <Typography sx={{ fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 30, letterSpacing: '-0.5px', mb: 1 }}>Today's review</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {concepts.length} concept{concepts.length === 1 ? '' : 's'} · ~{estimatedMinutes} min
        </Typography>
        <Card elevation={1} sx={{ mb: 2 }}>
          <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
            {Object.entries(bookCounts).map(([book, count]) => (
              <Box key={book} sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.5 }}>
                <Typography variant="body2">{book}</Typography>
                <Typography variant="body2" color="text.secondary">{count} concept{count === 1 ? '' : 's'}</Typography>
              </Box>
            ))}
          </Stack>
        </Card>
        {topics.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {topics.map(t => (
              <Chip key={t} label={t} size="small" variant="outlined" />
            ))}
          </Box>
        )}
        <Button variant="contained" onClick={startSession} size="large">Begin Review</Button>
      </Box>
    )
  }

  if (state.phase === 'complete') {
    return (
      <SessionCompleteContent
        totalCount={state.totalCount}
        nextReviewInHours={state.nextReviewInHours}
        questionResults={state.questionResults}
        newMilestones={state.newMilestones ?? []}
        dailyGoalMet={state.dailyGoalMet ?? false}
        isFirstSession={state.isFirstSession}
        onDone={() => navigate('/today')}
      />
    )
  }

  const currentQ = state.session.concepts[state.currentIndex]
  const displayQuestion = state.jitQuestion ?? currentQ
  const isStreaming = state.streamingText !== null
  const canSubmit = !isStreaming && state.draftAnswer.trim().length > 0 && !state.conversationEnded && !!state.conversationSessionId && pendingAnswer === null
  const progress = Math.round(((state.currentIndex + 1) / state.session.concepts.length) * 100)

  return (
    <Box sx={{
      maxWidth: 680,
      mx: 'auto',
      // Fill the viewport minus AppShell chrome so the input footer stays on-screen (96px xs, 108px sm).
      height: { xs: 'calc(100vh - 96px)', sm: 'calc(100vh - 108px)' },
      display: 'flex',
      flexDirection: 'column',
    }}>

      <Box sx={{ flexShrink: 0, pb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: state.isFirstSession ? 'flex-end' : 'space-between', alignItems: 'center', mb: 1 }}>
          {!state.isFirstSession && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Concept {state.currentIndex + 1} / {state.session.concepts.length}
            </Typography>
          )}
          <Button
            variant="text"
            size="small"
            onClick={() => setShowSkipDialog(true)}
            disabled={isStreaming || !state.conversationSessionId}
            sx={{ color: 'text.secondary', minWidth: 0 }}
          >
            Skip
          </Button>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1, height: 4, mb: 2 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {currentQ.bookTitle} · {currentQ.bookAuthor}
        </Typography>
        {currentQ.question.specificTopic && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
            {currentQ.question.specificTopic}
          </Typography>
        )}
        {state.isFirstSession && state.currentIndex === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {FIRST_QUESTION_TASK}
          </Typography>
        )}
        <Typography sx={{
          fontFamily: 'var(--font-serif)',
          fontSize: 22,
          lineHeight: 1.3,
          letterSpacing: '-0.3px',
          color: 'var(--ink)',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
        }}>
          {state.isStartingConversation ? '…' : displayQuestion.question.questionText}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 2, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>

        {state.isStartingConversation && <TypingDots />}

        {state.conversationHistory.map((turn, i) => (
          <Box key={i} sx={{ mb: 4 }}>
            <UserBubble text={turn.userAnswer} />
            <ConfidenceSection selectedLabel={turn.confidenceLabel ?? null} />
            {hasTutorContent(turn.result) && (
              <Box sx={{ mt: 2 }}>
                <AiMessage result={turn.result} isFirstSession={state.isFirstSession} />
              </Box>
            )}
          </Box>
        ))}

        {pendingAnswer !== null && (
          <Box sx={{ mb: 2 }}>
            <UserBubble text={pendingAnswer} />
            <ConfidenceSection
              selectedLabel={pendingConfidenceLabel}
              onSelect={(value, label) => {
                setPendingConfidenceLabel(label)
                submitAnswer(value as 'FORGOT' | 'HARD' | 'GOOD' | 'EASY', label)
              }}
            />
          </Box>
        )}

        {isStreaming && (
          state.streamingText === ''
            ? isRetrying
              ? <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 1 }}>{RETRY_MESSAGES[retryMsgIdx]}</Typography>
              : <TypingDots />
            : (
                <Box sx={{ py: 1 }}>
                  {(state.streamingSegments ?? []).map((segment, i) => (
                    <StreamingSegmentBlock key={i} segment={segment} isFirst={i === 0} />
                  ))}
                </Box>
              )
        )}

        <div ref={messageEndRef} />
      </Box>

      <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>

        {state.phase === 'conversation' && state.conversationError && (
          <Box sx={{ pt: 1.5 }}>
            {state.conversationError === 'HTTP_402' ? (
              <Alert severity="warning" action={
                <Button size="small" color="inherit" onClick={() => navigate('/settings')}>Settings</Button>
              }>
                Subscription required. Please check your plan in Settings.
              </Alert>
            ) : state.conversationError === 'HTTP_400' ? (
              <Alert severity="error" action={
                <Button size="small" color="inherit" onClick={() => { clearConversationError(); setPendingAnswer(null); setPendingConfidenceLabel(null); setShowSkipDialog(true) }}>Skip</Button>
              }>
                This question is no longer available.
              </Alert>
            ) : state.conversationError === 'HTTP_401' ? (
              <Alert severity="error">
                Session expired. Please sign out and sign back in.
              </Alert>
            ) : state.conversationError === 'timeout' ? (
              <Alert severity="error" action={
                <Button size="small" color="inherit" onClick={() => { clearConversationError(); setPendingAnswer(null); setPendingConfidenceLabel(null) }}>Retry</Button>
              }>
                Evaluation timed out. Please retry.
              </Alert>
            ) : (
              <Alert severity="error" action={
                <Button size="small" color="inherit" onClick={() => { clearConversationError(); setPendingAnswer(null); setPendingConfidenceLabel(null) }}>Retry</Button>
              }>
                Connection error. Please try again.
              </Alert>
            )}
          </Box>
        )}

        {!state.conversationEnded && !state.isStartingConversation && pendingAnswer === null && (
          <Box sx={{ pt: 1.5, pb: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                multiline
                maxRows={4}
                fullWidth
                placeholder="Type your answer..."
                value={state.draftAnswer}
                onChange={e => setDraftAnswer(e.target.value)}
                disabled={isStreaming || !state.conversationSessionId}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSubmit) {
                    e.preventDefault()
                    setPendingAnswer(state.draftAnswer)
                  }
                }}
              />
              <IconButton
                onClick={() => setPendingAnswer(state.draftAnswer)}
                disabled={!canSubmit}
                sx={{
                  mb: '4px',
                  bgcolor: canSubmit ? 'primary.main' : 'action.disabledBackground',
                  color: canSubmit ? 'primary.contrastText' : 'action.disabled',
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
              ⌘↵ or Ctrl↵ to send
            </Typography>
          </Box>
        )}

        {state.isStartingConversation && (
          <Box sx={{ pt: 1.5, pb: { xs: 1.5, sm: 2 } }}>
            <Typography variant="body2" color="text.disabled">Preparing question...</Typography>
          </Box>
        )}

        {state.conversationEnded && !isStreaming && (
          <Box sx={{ pt: 1.5, pb: { xs: 1.5, sm: 2 } }}>
            <Button variant="contained" onClick={nextQuestion}>
              {state.currentIndex + 1 >= state.session.concepts.length ? 'Finish' : 'Next concept'}
            </Button>
          </Box>
        )}

      </Box>

      {showSkipDialog && (
        <ConfirmDialog
          title="Skip this question?"
          message="The question will be marked as incorrect and your progress on this turn will be lost."
          confirmLabel="Skip"
          onConfirm={() => { setShowSkipDialog(false); skipQuestion() }}
          onCancel={() => setShowSkipDialog(false)}
        />
      )}
    </Box>
  )
}
